import { RefObject, useEffect, useRef } from 'react';
import { Id, toast } from 'react-toastify';
import {
    evictOverflowToasts,
    getFrontToastId,
    getToastContainerElement,
} from './toast-stack-controller';

const DEFAULT_AUTO_CLOSE_MS = 3000;

function nowMs(): number {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
        return performance.now();
    }

    return Date.now();
}

function parseAutoCloseMs(data: unknown): number | false {
    if (!data || typeof data !== 'object' || !('autoCloseMs' in data)) {
        return DEFAULT_AUTO_CLOSE_MS;
    }

    const { autoCloseMs } = data as { autoCloseMs?: number | false };

    if (autoCloseMs === false) {
        return false;
    }

    if (typeof autoCloseMs === 'number' && autoCloseMs > 0) {
        return autoCloseMs;
    }

    return DEFAULT_AUTO_CLOSE_MS;
}

export function useFrontToastTimer(containerRef: RefObject<HTMLElement | null>) {
    const timerRef = useRef<ReturnType<typeof setTimeout>>();
    const durationsRef = useRef<Map<string, number | false>>(new Map());
    // The toast the timer is currently tracking (the front/newest one).
    const frontIdRef = useRef<string | undefined>();
    // Time left on the front toast: a number of ms, or false when it must persist.
    const remainingRef = useRef<number | false>(false);
    // Absolute deadline (nowMs()-based) while the timer is actively running.
    const deadlineRef = useRef<number | undefined>();
    // Whether the pointer is currently over the toast stack (pauses the timer).
    const hoveredRef = useRef(false);

    useEffect(() => {
        const clearTimer = () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = undefined;
            }

            deadlineRef.current = undefined;
        };

        const startTimer = (toastId: string, ms: number) => {
            clearTimer();
            deadlineRef.current = nowMs() + ms;
            remainingRef.current = ms;
            timerRef.current = setTimeout(() => {
                toast.dismiss(toastId);
            }, ms);
        };

        const pauseTimer = () => {
            if (!timerRef.current || frontIdRef.current === undefined) {
                return;
            }

            const remaining = deadlineRef.current !== undefined
                ? deadlineRef.current - nowMs()
                : remainingRef.current;

            remainingRef.current = typeof remaining === 'number' ? Math.max(0, remaining) : remaining;
            clearTimer();
        };

        const resumeTimer = () => {
            if (frontIdRef.current === undefined || timerRef.current) {
                return;
            }

            const remaining = remainingRef.current;

            if (remaining === false) {
                return;
            }

            startTimer(frontIdRef.current, remaining ?? DEFAULT_AUTO_CLOSE_MS);
        };

        const scheduleFrontDismiss = (toastId?: Id) => {
            clearTimer();

            const container = getToastContainerElement(containerRef);
            const frontToastId = toastId ?? (container ? getFrontToastId(container) : undefined);

            if (!frontToastId) {
                frontIdRef.current = undefined;
                remainingRef.current = false;
                return;
            }

            const normalizedId = String(frontToastId);
            const duration = durationsRef.current.get(normalizedId);

            frontIdRef.current = normalizedId;

            if (duration === false) {
                remainingRef.current = false;
                return;
            }

            const durationMs = duration ?? DEFAULT_AUTO_CLOSE_MS;

            // Hold the countdown while the pointer is over the stack; it resumes on mouse-out.
            if (hoveredRef.current) {
                remainingRef.current = durationMs;
                return;
            }

            startTimer(normalizedId, durationMs);
        };

        const root = containerRef.current;

        const handlePointerOver = () => {
            if (hoveredRef.current) {
                return;
            }

            hoveredRef.current = true;
            pauseTimer();
        };

        const handlePointerOut = (event: MouseEvent) => {
            const nextTarget = event.relatedTarget as Node | null;

            // Ignore moves between toasts within the same stack.
            if (root && nextTarget && root.contains(nextTarget)) {
                return;
            }

            if (!hoveredRef.current) {
                return;
            }

            hoveredRef.current = false;
            resumeTimer();
        };

        if (root) {
            root.addEventListener('mouseover', handlePointerOver);
            root.addEventListener('mouseout', handlePointerOut);
        }

        const unsubscribe = toast.onChange((item) => {
            const normalizedId = String(item.id);

            if (item.status === 'added') {
                durationsRef.current.set(normalizedId, parseAutoCloseMs(item.data));

                window.requestAnimationFrame(() => {
                    const container = getToastContainerElement(containerRef);

                    if (container) {
                        evictOverflowToasts(container);
                    }

                    scheduleFrontDismiss(item.id);
                });
                return;
            }

            if (item.status === 'removed') {
                durationsRef.current.delete(normalizedId);

                window.requestAnimationFrame(() => {
                    scheduleFrontDismiss();
                });
                return;
            }

            if (item.status === 'updated') {
                durationsRef.current.set(normalizedId, parseAutoCloseMs(item.data));

                const container = getToastContainerElement(containerRef);

                if (container && normalizedId === getFrontToastId(container)) {
                    scheduleFrontDismiss(item.id);
                }
            }
        });

        return () => {
            unsubscribe();
            clearTimer();

            if (root) {
                root.removeEventListener('mouseover', handlePointerOver);
                root.removeEventListener('mouseout', handlePointerOut);
            }
        };
    }, [containerRef]);
}
